import { SimplexNoise } from '../../util/SimplexNoise';
import { Ray } from '../../math/Ray';
import { HeightField, HeightFieldHit, HeightFieldSample } from './HeightField';
import { centralDifferenceNormal, raycastHeightField } from './heightfield-raycast';

export type FalloffShape = 'island' | 'valley' | 'none' | string;

export interface NoiseHeightFieldConfig {
  seed: number | bigint;
  octaves: number;
  baseFrequency: number;
  amplitude: number;
  worldWidth: number;
  worldDepth: number;
  falloffShape: FalloffShape;
}

/**
 * TypeScript port of `noodle.scene.terrain.NoiseHeightSource`. Produces FBM
 * noise over OpenSimplex2 with optional radial falloff. Matches the Kotlin
 * reference closely; minor float-precision differences (Kotlin uses Float
 * accumulators while TS uses Double) are sub-millimeter at typical amplitudes.
 */
export class NoiseHeightField implements HeightField {
  private readonly noise: SimplexNoise;
  private readonly octaves: number;
  private readonly baseFreq: number;
  private readonly amplitude: number;
  private readonly halfW: number;
  private readonly halfD: number;
  private readonly width: number;
  private readonly depth: number;
  private readonly falloffShape: FalloffShape;
  private readonly normalEps: number;

  constructor(config: NoiseHeightFieldConfig) {
    this.noise = new SimplexNoise(config.seed);
    this.octaves = clamp(Math.trunc(config.octaves), 1, 8);
    this.baseFreq = Math.max(0.01, config.baseFrequency);
    this.amplitude = config.amplitude;
    this.width = Math.max(1, config.worldWidth);
    this.depth = Math.max(1, config.worldDepth);
    this.halfW = this.width * 0.5;
    this.halfD = this.depth * 0.5;
    this.falloffShape = config.falloffShape;
    // Scale derivative epsilon by frequency: high-frequency noise needs a smaller
    // sampling stencil to capture local slope. Floor at 0.25 m.
    this.normalEps = Math.max(0.25, 1 / (this.baseFreq * 8));
  }

  sampleHeight(x: number, z: number): number {
    const fx = x / this.width;
    const fz = z / this.depth;

    let h = 0;
    let amp = 1;
    let freq = this.baseFreq;
    let norm = 0;
    for (let o = 0; o < this.octaves; o++) {
      h += amp * this.noise.sample2d(fx * freq, fz * freq);
      norm += amp;
      amp *= 0.5;
      freq *= 2;
    }
    h = (h / norm) * this.amplitude;
    return h * this.applyFalloff(x, z);
  }

  sampleHeightWithNormal(x: number, z: number): HeightFieldSample {
    return centralDifferenceNormal(x, z, this.normalEps, (sx, sz) =>
      this.sampleHeight(sx, sz),
    )!;
  }

  raycast(ray: Ray, maxDistance?: number): HeightFieldHit | null {
    return raycastHeightField(
      ray,
      (x, z) => this.sampleHeight(x, z),
      (x, z) => this.sampleHeightWithNormal(x, z),
      maxDistance,
      Math.max(2, this.normalEps * 4),
    );
  }

  private applyFalloff(x: number, z: number): number {
    if (this.falloffShape !== 'island' && this.falloffShape !== 'valley') return 1;
    const nx = this.halfW > 0 ? x / this.halfW : 0;
    const nz = this.halfD > 0 ? z / this.halfD : 0;
    const r = clamp(Math.sqrt(nx * nx + nz * nz), 0, 1);
    if (this.falloffShape === 'island') {
      const t = clamp(1 - r * r, 0, 1);
      return t * t;
    }
    // valley
    const t = clamp((r - 0.3) / 0.7, 0, 1);
    return 0.5 * (1 - Math.cos(t * Math.PI));
  }
}

function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}
